import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Alert, Button, Drawer, Empty, Icon, Input, Popconfirm, Select, Skeleton, Tag, message } from 'antd';
import styled from 'styled-components';

import { request } from '~/_shared/instance';
import { department as departmentName } from '~/components/RegisterTasks/Utils';
import { loadTasks } from '~/components/RegisterTasks/actions';
import {
  REGIMES, ACTIVITIES, PAYROLL, COVERAGES, coverageLabel, describeRule, hasRule,
} from './enrollment';
import { say, Flow, Block, BlockHead, Step, Title, Options, Chip, ChoiceBlock, Joiner } from './ruleUi';

// ---------------------------------------------------------------------------
// Grupos de tarefas, no Cadastro de Tarefas.
//
// Um grupo junta tarefas irmãs (TFE janeiro, TFE fevereiro...) para o cliente
// ser ligado nelas de uma vez e para dar a todas a mesma regra. A cobertura
// diz em quantas filhas cada cliente do grupo precisa estar.
// ---------------------------------------------------------------------------

const DEPARTMENTS = [1, 2, 3, 4, 5, 6];

const Card = styled.button`
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  margin-bottom: 8px;
  padding: 12px 14px;
  border: 1px solid #e8e8e8;
  border-radius: 10px;
  background: #fff;
  text-align: left;
  cursor: pointer;

  &:hover { border-color: #91d5ff; background: #f6fbff; }

  .body { flex: 1; min-width: 0; }
  strong { display: block; font-size: 14px; color: #262626; }
  small { display: block; font-size: 12px; color: #8c8c8c; }
`;

const Field = styled.div`
  margin-bottom: 14px;

  label {
    display: block;
    margin-bottom: 4px;
    font-size: 13px;
    font-weight: 500;
    color: #262626;
  }
`;

const Help = styled.div`
  margin-top: 6px;
  font-size: 12px;
  color: #8c8c8c;
`;

const emptyGroup = () => ({
  id: null, name: '', department: null, coverage: null, task_ids: [],
  enrollment_rule: { regimes: [], activities: [], payroll: [] },
});

const ruleOf = (group) => {
  const rule = group.enrollment_rule || {};
  return {
    regimes: rule.regimes || [],
    activities: rule.activities || [],
    payroll: rule.payroll || [],
  };
};

const TaskGroups = ({ visible, onClose, tasks }) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(true);
  const [groups, setGroups] = useState([]);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const allTasks = tasks || [];
  const taskById = useMemo(() => {
    const map = {};
    allTasks.forEach((task) => { map[task.id] = task; });
    return map;
  }, [allTasks]);

  const load = async () => {
    setLoading(true);
    try {
      const response = await request('GET', '/task-groups', null, false);
      setGroups((response && response.data && response.data.data) || []);
    } catch (error) {
      // o interceptor da request já avisa
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (visible) {
      setEditing(null);
      load();
    }
  }, [visible]);

  const set = (patch) => setEditing(Object.assign({}, editing, patch));
  const setRule = (patch) => set({ enrollment_rule: Object.assign({}, ruleOf(editing), patch) });

  // Tarefas cujo nome contém o nome do grupo e ainda não estão nele: é o
  // atalho para montar "TFE" com as 12 TFEs de uma vez.
  const byName = useMemo(() => {
    if (!editing || editing.name.trim().length < 2) return [];
    const term = editing.name.trim().toLowerCase();
    return allTasks.filter((task) => (
      String(task.name || '').toLowerCase().indexOf(term) > -1 && editing.task_ids.indexOf(task.id) === -1
    ));
  }, [editing, allTasks]);

  const inOtherGroup = (task) => task.group_id && (!editing || task.group_id !== editing.id);
  const movedIn = editing ? editing.task_ids.filter((id) => taskById[id] && inOtherGroup(taskById[id])) : [];

  const save = async () => {
    setSaving(true);
    try {
      const rule = ruleOf(editing);
      const body = {
        name: editing.name.trim(),
        department: editing.department,
        coverage: editing.coverage,
        enrollment_rule: rule.regimes.length > 0 ? rule : null,
        task_ids: editing.task_ids,
      };
      await request(editing.id ? 'PUT' : 'POST', '/task-groups' + (editing.id ? '/' + editing.id : ''), body, false);
      message.success(editing.id ? 'Grupo atualizado.' : 'Grupo criado.');
      setEditing(null);
      load();
      dispatch(loadTasks({ showLoading: false }));
    } catch (error) {
      // o interceptor da request já avisa
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    try {
      await request('DELETE', '/task-groups/' + editing.id, null, false);
      message.success('Grupo excluído. As tarefas continuam cadastradas.');
      setEditing(null);
      load();
      dispatch(loadTasks({ showLoading: false }));
    } catch (error) {
      // o interceptor da request já avisa
    }
  };

  const renderList = () => {
    if (loading) return <Skeleton active />;
    return (
      <>
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message="Grupos juntam tarefas irmãs, como TFE janeiro, TFE fevereiro…"
          description="No cliente, o grupo vira uma linha com um botão só. A regra do grupo vale para todas as filhas, e a cobertura diz em quantas delas o cliente precisa estar."
        />
        {groups.length === 0 ? (
          <Empty description="Nenhum grupo ainda" />
        ) : groups.map((group) => (
          <Card key={group.id} type="button" onClick={() => setEditing(Object.assign(emptyGroup(), group))} data-group-card={group.name}>
            <Icon type="folder" style={{ fontSize: 20, color: '#faad14' }} />
            <div className="body">
              <strong>{group.name}</strong>
              <small>
                {say(group.task_ids.length, '1 tarefa', group.task_ids.length + ' tarefas')}
                {group.department ? ' · ' + departmentName(group.department) : ''}
                {' · '}{hasRule(group) ? describeRule(group) : 'sem regra'}
              </small>
            </div>
            <Tag>{coverageLabel(group.coverage)}</Tag>
            <Icon type="right" style={{ color: '#bfbfbf' }} />
          </Card>
        ))}
        <Button type="dashed" block icon="plus" onClick={() => setEditing(emptyGroup())} data-new-group>
          Novo grupo
        </Button>
      </>
    );
  };

  const renderForm = () => {
    const rule = ruleOf(editing);
    const noRegime = rule.regimes.length === 0;
    const coverage = COVERAGES.filter((c) => c.key === (editing.coverage || null))[0];
    return (
      <Flow data-group-form>
        <Button type="link" icon="arrow-left" style={{ padding: 0, marginBottom: 12 }} onClick={() => setEditing(null)}>
          Todos os grupos
        </Button>

        <Field>
          <label>Nome do grupo</label>
          <Input value={editing.name} placeholder="Ex.: TFE" onChange={(event) => set({ name: event.target.value })} data-group-name />
        </Field>

        <Field>
          <label>Departamento</label>
          <Select allowClear placeholder="Departamento" style={{ width: '100%' }} value={editing.department || undefined}
            onChange={(value) => set({ department: value || null })}>
            {DEPARTMENTS.map((d) => <Select.Option key={d} value={d}>{departmentName(d)}</Select.Option>)}
          </Select>
        </Field>

        <Field>
          <label>Tarefas do grupo · {editing.task_ids.length}</label>
          <Select
            mode="multiple"
            showSearch
            optionFilterProp="children"
            placeholder="Escolha as tarefas"
            style={{ width: '100%' }}
            value={editing.task_ids}
            onChange={(ids) => set({ task_ids: ids })}
            data-group-tasks
          >
            {allTasks.slice().sort((a, b) => String(a.name).localeCompare(String(b.name))).map((task) => (
              <Select.Option key={task.id} value={task.id}>{task.name}</Select.Option>
            ))}
          </Select>
          {byName.length > 0 && (
            <Button size="small" type="link" icon="plus" style={{ padding: 0, marginTop: 6 }}
              onClick={() => set({ task_ids: editing.task_ids.concat(byName.map((task) => task.id)) })} data-add-by-name>
              Adicionar {say(byName.length, 'a tarefa', 'as ' + byName.length + ' tarefas')} com “{editing.name.trim()}” no nome
            </Button>
          )}
          {movedIn.length > 0 && (
            <Help>
              <Icon type="swap" /> {say(movedIn.length, '1 tarefa sai', movedIn.length + ' tarefas saem')} de outro grupo:
              uma tarefa fica em um grupo só.
            </Help>
          )}
        </Field>

        <Block data-axis="coverage">
          <BlockHead>
            <Step>1</Step>
            <Title>
              Cobertura
              <small>Em quantas tarefas do grupo cada cliente precisa estar.</small>
            </Title>
          </BlockHead>
          <Options>
            {COVERAGES.map((c) => (
              <Chip key={String(c.key)} type="button" aria-pressed={(editing.coverage || null) === c.key}
                data-chip={c.label} onClick={() => set({ coverage: c.key })}>
                {(editing.coverage || null) === c.key && <Icon type="check" />}
                {c.label}
              </Chip>
            ))}
          </Options>
          <Help>{coverage.help}</Help>
        </Block>

        <Joiner />

        <ChoiceBlock
          axis="regimes"
          step="2"
          title="Para quem o grupo é sugerido · regime"
          help={noRegime
            ? (editing.coverage ? 'Sem regime: a cobertura vale para todos os clientes.' : 'Sem regime: o grupo é escolhido à mão, cliente a cliente.')
            : 'Vale para as filhas no lugar da regra de cada uma.'}
          options={REGIMES}
          value={rule.regimes}
          onChange={(regimes) => setRule({ regimes })}
        />
        <Joiner />
        <ChoiceBlock
          axis="activities"
          step="3"
          title="Atividade"
          help="Vazio é qualquer atividade."
          options={ACTIVITIES}
          value={rule.activities}
          onChange={(activities) => setRule({ activities })}
          anyLabel="Qualquer"
          disabled={noRegime}
        />
        <Joiner />
        <ChoiceBlock
          axis="payroll"
          step="4"
          title="Folha de pagamento"
          help="Vazio é com ou sem folha."
          options={PAYROLL}
          value={rule.payroll}
          onChange={(payroll) => setRule({ payroll })}
          anyLabel="Qualquer"
          disabled={noRegime}
        />

        <div style={{ display: 'flex', gap: 8, justifyContent: 'space-between', marginTop: 20 }}>
          {editing.id ? (
            <Popconfirm title="Excluir o grupo? As tarefas e os clientes delas continuam como estão." okText="Excluir" okType="danger" cancelText="Cancelar" onConfirm={remove}>
              <Button type="danger" ghost icon="delete">Excluir grupo</Button>
            </Popconfirm>
          ) : <span />}
          <Button type="primary" icon="save" loading={saving}
            disabled={!editing.name.trim() || editing.task_ids.length === 0}
            onClick={save} data-save-group>
            Salvar grupo
          </Button>
        </div>
      </Flow>
    );
  };

  return (
    <Drawer
      title={editing ? (editing.id ? 'Grupo · ' + editing.name : 'Novo grupo') : 'Grupos de tarefas'}
      visible={visible}
      onClose={onClose}
      width={640}
      destroyOnClose
    >
      {editing ? renderForm() : renderList()}
    </Drawer>
  );
};

export default TaskGroups;
